import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Package, Ruler, Search, Weight } from "lucide-react";
import { getOrderItemListApproved } from "../../../reducers/order.slice";
import { createShipment } from "../../../reducers/shipment.slice";
import { AuthContext } from "../../../context/authContext";
import { convertDateFormatInUtc, getDeadlineInfo, subtractHoursUtc } from "../../../utils/util";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import PageHeader from "../../../components/shared/PageHeader";
import QuantityStepper from "../../../components/shared/QuantityStepper";
import PolicyDeadlineBadge from "../../../components/shared/PolicyDeadlineBadge";
import useMediaQuery from "../../../components/shared/useMediaQuery";
import ShipmentBoxesReview from "../ShipmentBoxesReview";
import ShipmentRulesReview from "../ShipmentRulesReview";
import { SHIPMENT_BOX_LIMIT_HELPER, getBoxRuleErrors, getShipmentEstimatedValue, hasBoxRuleErrors } from "../shipmentRules";

const panelStyle = {
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 10,
  background: "rgba(255,255,255,0.02)",
};

const CreateShipmentPage = ({ dialogMode = false, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [createLoading, setCreateLoading] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [totalBox, setTotalBox] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [boxPhase, setBoxPhase] = useState("items");
  const [createFormData, setCreateFormData] = useState([]);
  const [upcOptions, setUpcOptions] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [replicateBoxes, setReplicateBoxes] = useState(false);
  const [numberOfBoxesToReplicate, setNumberOfBoxesToReplicate] = useState(1);
  const [rulesAcknowledged, setRulesAcknowledged] = useState(false);

  useEffect(() => {
    setCreateLoading(true);
    dispatch(getOrderItemListApproved()).then((res) => {
      setCreateLoading(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedOptions = res?.payload?.map((item) => {
        const approvalBaseDate =
          item?.order_status_updated_at || subtractHoursUtc(item?.order_expiry);
        const priceLockDeadlineInfo = getDeadlineInfo(approvalBaseDate);

        return {
          upc: item?.upc,
          quantity: item?.quantity,
          item_name: item?.item_name,
          order_id: item?.order_id,
          item_id: item?.id,
          buy_price: item?.buy_price || 0,
          cashout_price: item?.cashout_price,
          allowedQty: item?.quantity - item?.shipped_quantity,
          originalAllowedQty: item?.quantity - item?.shipped_quantity,
          approvalDate: approvalBaseDate
            ? convertDateFormatInUtc(approvalBaseDate, "MMM D, YYYY · h:mm A")
            : "—",
          createShipmentBy: priceLockDeadlineInfo
            ? convertDateFormatInUtc(priceLockDeadlineInfo.deadline, "MMM D, YYYY · h:mm A")
            : "—",
          priceLockDeadlineInfo,
        };
      });
      setUpcOptions(formattedOptions || []);
    });
  }, []);

  const isBoxComplete = (stepIndex) => {
    const box = createFormData[stepIndex];
    if (!box) return false;
    const dimensionsComplete = Object.values(box.box_dimension).every((v) => v !== "" && v !== null && v !== "0" && v !== "0.0");
    const itemsComplete = box.box_items.length > 0 && box.box_items.every((item) => item.quantity > 0);
    return dimensionsComplete && itemsComplete;
  };

  const getIncompleteBoxStep = () => {
    const boxCount = Number(totalBox) || 0;
    for (let i = 0; i < boxCount; i += 1) {
      const boxData = createFormData[i];
      if (!boxData || boxData.box_items.length === 0 || boxData.box_items.some((item) => !(item.quantity > 0))) {
        return { step: i + 1, phase: "items" };
      }
      const dimensionsComplete = Object.values(boxData.box_dimension).every((v) => v !== "" && v !== null && v !== "0" && v !== "0.0");
      if (!dimensionsComplete) {
        return { step: i + 1, phase: "dimensions" };
      }
    }
    return null;
  };

  const advanceAfterCurrentBox = () => {
    if (activeStep < totalBox) {
      setActiveStep((prev) => prev + 1);
      setBoxPhase("items");
      setItemSearchTerm("");
      return;
    }

    const incompleteBox = getIncompleteBoxStep();
    if (incompleteBox) {
      setActiveStep(incompleteBox.step);
      setBoxPhase(incompleteBox.phase);
      setItemSearchTerm("");
      return;
    }

    setActiveStep(totalBox + 1);
  };

  const handleCreateTotalBoxChange = (numValue) => {
    setRulesAcknowledged(false);
    setTotalBox(numValue);
    setCreateFormData((prev) => {
      const newData = [...prev];
      while (newData.length > numValue) newData.pop();
      while (newData.length < numValue) {
        newData.push({
          box_items: [],
          box_dimension: { height: "", width: "", length: "", weight: "" },
          dimensions_copied: false,
        });
      }
      return newData;
    });
  };

  const handleCreateAddBox = () => {
    setRulesAcknowledged(false);
    const newTotal = totalBox === null || totalBox === 0 || !totalBox ? 1 : parseInt(totalBox) + 1;
    setTotalBox(newTotal);
    setCreateFormData((prev) => [
      ...prev,
      { box_items: [], box_dimension: { height: "", width: "", length: "", weight: "" }, dimensions_copied: false },
    ]);
  };

  const handleCreateReduceBox = () => {
    if (totalBox > 0) {
      setRulesAcknowledged(false);
      setTotalBox(parseInt(totalBox) - 1);
      setCreateFormData((prev) => prev.slice(0, -1));
    }
  };

  const handleCreateFormDataChange = (index, field, value) => {
    setRulesAcknowledged(false);
    const removeLeadingZeros = (val) => {
      if (val === null || val === "") return val;
      if (typeof val === "string" && /^0(\.\d+)?$/.test(val)) return val;
      if (typeof val === "string") return val.replace(/^0+(?!\.)/, "");
      return val;
    };

    setCreateFormData((prev) => {
      const updated = [...prev];
      if (field === "box_items") {
        updated[index].box_items = value.map((item_id) => {
          const itemDetails = upcOptions.find((option) => option.item_id === item_id);
          return {
            ...itemDetails,
            quantity: updated[index].box_items.find((item) => item.item_id === item_id)?.quantity || 1,
          };
        });
      } else if (field in updated[index].box_dimension) {
        updated[index].box_dimension[field] = removeLeadingZeros(value);
        updated[index].dimensions_copied = false;
      }
      updated[index].box_name = `Box ${index + 1}`;
      return updated;
    });
  };

  const toggleItemSelection = (boxIndex, itemId) => {
    const current = createFormData[boxIndex]?.box_items.map((item) => item.item_id) || [];
    const next = current.includes(itemId) ? current.filter((id) => id !== itemId) : [...current, itemId];
    handleCreateFormDataChange(boxIndex, "box_items", next);
  };

  const handleCreateQuantityChange = (boxIndex, item_id, quantity) => {
    setRulesAcknowledged(false);
    const numericQuantity = Number(quantity) || 0;
    setCreateFormData((prev) => {
      const updated = [...prev];
      const item = updated[boxIndex].box_items.find((item) => item.item_id === item_id);
      if (item) item.quantity = numericQuantity;
      return updated;
    });
    setUpcOptions((prev) =>
      prev.map((item) => {
        if (item.item_id === item_id) {
          const totalUsedExceptCurrent =
            Object.values(createFormData)
              .flatMap((box) => box.box_items)
              .filter((boxItem) => boxItem.item_id === item_id)
              .reduce((sum, boxItem) => sum + (Number(boxItem.quantity) || 0), 0) - numericQuantity;
          return { ...item, allowedQty: item.originalAllowedQty - totalUsedExceptCurrent };
        }
        return item;
      })
    );
  };

  const getRemainingAllowedQty = (item_id, currentBoxIndex) => {
    const option = upcOptions.find((item) => item.item_id === item_id);
    if (!option) return 0;
    const totalUsedQuantity = createFormData.reduce((sum, box, boxIndex) => {
      if (boxIndex === currentBoxIndex) return sum;
      const boxItem = box.box_items.find((item) => item.item_id === item_id);
      return sum + (Number(boxItem?.quantity) || 0);
    }, 0);
    const remainingQty = option.originalAllowedQty - totalUsedQuantity;
    return remainingQty > 0 ? remainingQty : 0;
  };

  const handleReplicateBoxCountChange = (value) => {
    const maxBoxes = Math.max(totalBox - activeStep, 1);
    const numValue = Number(value) || 1;
    setNumberOfBoxesToReplicate(Math.max(1, Math.min(maxBoxes, numValue)));
  };

  const validateBoxes = (boxDetails) => {
    const itemTracker = new Map();
    for (const box of boxDetails) {
      for (const item of box.box_items) {
        const key = `${item.item_id}_${item.upc}`;
        const existing = itemTracker.get(key) || { totalQty: 0, originalAllowedQty: item.originalAllowedQty };
        existing.totalQty += item.quantity;
        existing.originalAllowedQty = Math.min(existing.originalAllowedQty, item.originalAllowedQty);
        itemTracker.set(key, existing);
      }
    }
    for (const [, { totalQty, originalAllowedQty }] of itemTracker) {
      if (totalQty > originalAllowedQty) return { isValid: false };
    }
    return { isValid: true };
  };

  const handleWizardNext = () => {
    if (activeStep === 0) {
      if (!totalBox) return;
      setActiveStep(1);
      setBoxPhase("items");
      setItemSearchTerm("");
      return;
    }

    if (boxPhase === "items") {
      if (createFormData[activeStep - 1]?.dimensions_copied && isBoxComplete(activeStep - 1)) {
        advanceAfterCurrentBox();
        return;
      }
      setBoxPhase("dimensions");
      return;
    }

    const updatedUpcOptions = upcOptions.map((option) => {
      const totalUsedQuantity = createFormData.reduce((sum, box, boxIndex) => {
        if (boxIndex >= activeStep - 1) return sum;
        const boxItem = box.box_items.find((item) => item.item_id === option.item_id);
        return sum + (boxItem?.quantity || 0);
      }, 0);
      return { ...option, allowedQty: option.originalAllowedQty - totalUsedQuantity };
    });
    setUpcOptions(updatedUpcOptions);

    const hasExceededQuantities = createFormData[activeStep - 1]?.box_items.some((item) => {
      const updatedOption = updatedUpcOptions.find((opt) => opt.item_id === item.item_id);
      return item.quantity > (updatedOption?.allowedQty || 0);
    });
    if (hasExceededQuantities) {
      showToast("Quantities exceed available amounts", "error");
      return;
    }

    if (replicateBoxes && numberOfBoxesToReplicate > 0) {
      const currentBoxData = createFormData[activeStep - 1];
      const boxesToApply = parseInt(numberOfBoxesToReplicate, 10);

      setCreateFormData((prev) => {
        const updated = [...prev];
        for (let i = 0; i < boxesToApply; i++) {
          const newIndex = activeStep + i;
          if (!updated[newIndex]) continue;
          updated[newIndex] = { ...updated[newIndex], box_dimension: { ...currentBoxData.box_dimension }, dimensions_copied: true };
        }
        return updated;
      });

      setReplicateBoxes(false);
      setNumberOfBoxesToReplicate(1);
    }

    advanceAfterCurrentBox();
  };

  const handleWizardBack = () => {
    if (activeStep === totalBox + 1) {
      setRulesAcknowledged(false);
      setActiveStep(totalBox);
      setBoxPhase("dimensions");
      return;
    }
    if (boxPhase === "dimensions") {
      setRulesAcknowledged(false);
      setBoxPhase("items");
      return;
    }
    if (activeStep === 1) {
      setActiveStep(0);
    } else {
      setActiveStep((prev) => prev - 1);
      setBoxPhase("dimensions");
    }
  };

  const handleCreateSubmit = () => {
    const payload = { status: "LABEL_PENDING", box_details: createFormData };
    if (payload.box_details.some((box) => hasBoxRuleErrors(box.box_dimension))) {
      showToast("Please fix box weight and dimension limits before creating this shipment.", "error");
      return;
    }
    if (!rulesAcknowledged) {
      showToast("Please confirm the shipment rules before creating this shipment.", "error");
      return;
    }
    const result = validateBoxes(payload.box_details);
    if (!result.isValid) {
      showToast("Quantities exceed approved limits - please review your boxes", "error");
      return;
    }
    setCreateSubmitting(true);
    dispatch(createShipment(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setCreateSubmitting(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setCreateSubmitting(false);
      showToast("Shipment created successfully", "success");
      if (dialogMode && onSuccess) onSuccess();
      else navigate("/dashboard/shipment");
    });
  };

  const isReview = Boolean(totalBox && activeStep === totalBox + 1);
  const boxIndex = activeStep - 1;
  const box = createFormData[boxIndex];
  const boxRuleErrors = getBoxRuleErrors(box?.box_dimension);
  const hasCurrentBoxRuleErrors = Object.keys(boxRuleErrors).length > 0;
  const estimatedShipmentValue = getShipmentEstimatedValue(createFormData);
  const hasCurrentBoxItemLimitErrors = Boolean(
    box?.box_items?.some((item) => item.quantity > getRemainingAllowedQty(item.item_id, boxIndex))
  );
  const currentBoxUsesCopiedDimensions = Boolean(box?.dimensions_copied);
  const nextButtonLabel =
    boxPhase === "items"
      ? currentBoxUsesCopiedDimensions
        ? activeStep === totalBox
          ? "Next: Review"
          : `Next: Box ${activeStep + 1}`
        : "Next: Dimensions"
      : activeStep === totalBox
        ? "Next: Review"
        : `Next: Box ${activeStep + 1}`;

  const title = activeStep === 0 ? "Create Shipment" : isReview ? "Review & Submit" : `Box ${activeStep} of ${totalBox}`;
  const description =
    activeStep === 0
      ? "How many boxes are you shipping? You'll set the contents and dimensions for each one."
      : isReview
        ? "Confirm everything looks right, then create your shipment."
        : boxPhase === "items"
          ? "Select approved items and set how many go in this box."
          : "Enter this box's dimensions and weight.";

  return (
    <div style={{ width: "100%", display: "flex", justifyContent: "center", padding: dialogMode ? 0 : isMobile ? "8px 0 32px" : "24px 0 48px" }}>
      {(createLoading || createSubmitting) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {createSubmitting ? "Creating..." : "Loading your approved items..."}
        </div>
      )}

      <div style={{ width: "100%", maxWidth: 760 }}>
        {!dialogMode && (
          <>
            <button
              type="button"
              onClick={() => {
                activeStep === 0 ? navigate(-1) : handleWizardBack();
              }}
              disabled={createSubmitting}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#c3c8cc",
                background: "transparent",
                border: "none",
                padding: 0,
                marginBottom: 18,
                cursor: createSubmitting ? "not-allowed" : "pointer",
                fontSize: 13,
              }}
            >
              <ArrowLeft size={16} /> Back
            </button>

            <PageHeader title={title} subtitle={description} />
          </>
        )}
        {dialogMode && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: "0 0 4px" }}>{title}</h3>
            <p style={{ fontSize: 13, color: "#c3c8cc", margin: 0 }}>{description}</p>
          </div>
        )}

        <div style={{ ...panelStyle, marginTop: 18, padding: isMobile ? 16 : 22 }}>
          {totalBox > 0 && activeStep > 0 && (
            <div style={{ display: "flex", marginBottom: 16 }}>
              {Array.from({ length: totalBox + 1 }, (_, i) => i + 1).map((n) => (
                <div
                  key={n}
                  style={{
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    marginRight: n <= totalBox ? 4 : 0,
                    background: n < activeStep || (n === activeStep && isReview) ? "#00ADB5" : n === activeStep ? "rgba(0,173,181,0.4)" : "#2a2a2e",
                  }}
                />
              ))}
            </div>
          )}

          {activeStep === 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 0" }}>
              <Package size={36} color="#c3c8cc" style={{ marginBottom: 14 }} />
              <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={handleCreateReduceBox}
                  disabled={!totalBox || totalBox <= 0}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: "#1e1e1e",
                    border: "1px solid #2a2a2e",
                    color: !totalBox || totalBox <= 0 ? "#4a4a4e" : "#fff",
                    fontSize: 18,
                    cursor: !totalBox || totalBox <= 0 ? "not-allowed" : "pointer",
                  }}
                >
                  -
                </button>
                <div style={{ width: 64, textAlign: "center", fontSize: 24, fontWeight: 800, color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                  {totalBox || 0}
                </div>
                <button
                  type="button"
                  onClick={handleCreateAddBox}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: "#1e1e1e",
                    border: "1px solid #2a2a2e",
                    color: "#fff",
                    fontSize: 18,
                    cursor: "pointer",
                  }}
                >
                  +
                </button>
              </div>
              <div style={{ fontSize: 12.5, color: "#c3c8cc", marginTop: 8 }}>
                box{totalBox === 1 ? "" : "es"}
              </div>
            </div>
          )}

          {activeStep >= 1 && !isReview && boxPhase === "items" && box && (
            <>
              {currentBoxUsesCopiedDimensions && (
                <div
                  style={{
                    marginBottom: 14,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(0,173,181,0.22)",
                    background: "rgba(0,173,181,0.06)",
                    color: "#c3c8cc",
                    fontSize: 12.5,
                    lineHeight: 1.45,
                  }}
                >
                  Dimensions and weight were applied from the previous box. Choose items and quantities for this box.
                </div>
              )}
              <div style={{ position: "relative", marginBottom: 16 }}>
                <Search size={14} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#c3c8cc" }} />
                <Input
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  placeholder="Search item, UPC, or order ID"
                  style={{ height: 40, fontSize: 13.5, padding: "0 12px 0 36px" }}
                />
              </div>
              {(() => {
                const selectedIds = box.box_items.map((i) => i.item_id);
                const query = itemSearchTerm.trim().toLowerCase();
                const eligibleOptions = upcOptions.filter((option) => {
                  const remaining = getRemainingAllowedQty(option.item_id, boxIndex);
                  return remaining > 0 || selectedIds.includes(option.item_id);
                });
                const visibleOptions = !query
                  ? eligibleOptions
                  : eligibleOptions.filter(
                      (option) =>
                        option.item_name?.toLowerCase().includes(query) ||
                        option.upc?.toLowerCase().includes(query) ||
                        option.order_id?.toLowerCase().includes(query)
                    );

                if (eligibleOptions.length === 0) {
                  return (
                    <div style={{ padding: "32px 16px", textAlign: "center", color: "#c3c8cc", fontSize: 13.5 }}>
                      You have no approved order items left to ship right now.
                      <div style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.6 }}>
                        Items show up here once an order is approved and still has quantity you haven't shipped yet.
                      </div>
                    </div>
                  );
                }

                if (visibleOptions.length === 0) {
                  return (
                    <div style={{ padding: "32px 16px", textAlign: "center", color: "#c3c8cc", fontSize: 13.5 }}>
                      No items match "{itemSearchTerm.trim()}".
                      <div style={{ marginTop: 12 }}>
                        <Button variant="outline" size="sm" onClick={() => setItemSearchTerm("")}>
                          Clear search
                        </Button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {visibleOptions.map((option) => {
                      const isSelected = selectedIds.includes(option.item_id);
                      const remaining = getRemainingAllowedQty(option.item_id, boxIndex);
                      const selectedItem = box.box_items.find((i) => i.item_id === option.item_id);
                      const overLimit = isSelected && selectedItem?.quantity > remaining;

                      return (
                        <div
                          key={option.item_id}
                          style={{
                            display: "flex",
                            alignItems: isMobile ? "flex-start" : "center",
                            flexDirection: isMobile ? "column" : "row",
                            padding: "14px 16px",
                            borderRadius: 10,
                            border: `1px solid ${overLimit ? "rgba(242,112,89,0.4)" : isSelected ? "rgba(0,173,181,0.35)" : "rgba(255,255,255,0.08)"}`,
                            background: isSelected ? "rgba(0,173,181,0.07)" : "rgba(255,255,255,0.015)",
                            transition: "background 0.12s, border-color 0.12s",
                            gap: isMobile ? 10 : 0,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0 }}>
                            <button
                              type="button"
                              aria-label={isSelected ? "remove item" : "add item"}
                              onClick={() => toggleItemSelection(boxIndex, option.item_id)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 20,
                                height: 20,
                                marginRight: 14,
                                borderRadius: 6,
                                border: isSelected ? "1px solid #00ADB5" : "1px solid rgba(255,255,255,0.25)",
                                background: isSelected ? "rgba(0,173,181,0.18)" : "transparent",
                                color: "#00ADB5",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                            >
                              {isSelected && <Check size={12} />}
                            </button>
                            <div onClick={() => toggleItemSelection(boxIndex, option.item_id)} style={{ flex: 1, minWidth: 0, cursor: "pointer", marginRight: isMobile ? 0 : 16 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: "#f2f4f5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {option.item_name}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", marginTop: 5 }}>
                                <span style={{ fontSize: 11.5, color: "#c3c8cc", fontFamily: "ui-monospace, SF Mono, Menlo, monospace", marginRight: 8 }}>
                                  UPC {option.upc} · Order {option.order_id}
                                </span>
                                <Badge variant={remaining > 0 ? "secondary" : "error"} style={{ fontFamily: "ui-monospace, SF Mono, Menlo, monospace" }}>
                                  {remaining} remaining
                                </Badge>
                              </div>
                              {overLimit && <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6, fontWeight: 600 }}>Exceeds remaining allowed quantity</div>}
                              {option.priceLockDeadlineInfo && (
                                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
                                  <span style={{ fontSize: 11.5, color: "#c3c8cc" }}>
                                    Create shipment by {option.createShipmentBy}
                                  </span>
                                  <PolicyDeadlineBadge
                                    info={option.priceLockDeadlineInfo}
                                    title="Orders created late may be repriced at current market rates."
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <QuantityStepper
                              value={selectedItem?.quantity ?? 0}
                              onChange={(next) => handleCreateQuantityChange(boxIndex, option.item_id, next)}
                              min={0}
                              max={remaining}
                              error={overLimit}
                              size="sm"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}

          {activeStep >= 1 && !isReview && boxPhase === "dimensions" && box && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                {[
                  ["height", "Height", "in", Ruler],
                  ["width", "Width", "in", Ruler],
                  ["length", "Length", "in", Ruler],
                  ["weight", "Weight", "lb", Weight],
                ].map(([field, fieldLabel, unit, FieldIcon]) => (
                  <div key={field}>
                    <Label htmlFor={`dim-${field}`} style={{ display: "flex", alignItems: "center" }}>
                      <FieldIcon size={13} style={{ marginRight: 6, color: "#c3c8cc" }} />
                      {fieldLabel}
                    </Label>
                    <div style={{ position: "relative" }}>
                      <Input
                        id={`dim-${field}`}
                        type="text"
                        inputMode="decimal"
                        value={box.box_dimension[field]}
                        onChange={(e) => {
                          const sanitized = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
                          handleCreateFormDataChange(boxIndex, field, sanitized);
                        }}
                        style={{ height: 42, fontSize: 14, padding: "0 40px 0 12px" }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: "#c3c8cc",
                          pointerEvents: "none",
                        }}
                      >
                        {unit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 10, fontSize: 11.5, color: "#8b9296" }}>{SHIPMENT_BOX_LIMIT_HELPER}</div>

              {hasCurrentBoxRuleErrors && (
                <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                  {boxRuleErrors.weight && <div style={{ fontSize: 11.5, color: "#f27059", fontWeight: 700 }}>{boxRuleErrors.weight}</div>}
                  {boxRuleErrors.dimensions && <div style={{ fontSize: 11.5, color: "#f27059", fontWeight: 700, lineHeight: 1.45 }}>{boxRuleErrors.dimensions}</div>}
                </div>
              )}

              {totalBox - activeStep > 0 && (
                <div style={{ marginTop: 22, padding: "14px 16px", ...panelStyle }}>
                  <label style={{ display: "flex", alignItems: "flex-start", fontSize: 13, color: "#c3c8cc", cursor: "pointer" }}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={replicateBoxes}
                      onClick={() => {
                        setReplicateBoxes((prev) => !prev);
                        setNumberOfBoxesToReplicate(1);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 18,
                        height: 18,
                        margin: "1px 10px 0 0",
                        borderRadius: 5,
                        border: replicateBoxes ? "1px solid #00ADB5" : "1px solid rgba(255,255,255,0.25)",
                        background: replicateBoxes ? "rgba(0,173,181,0.18)" : "transparent",
                        color: "#00ADB5",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {replicateBoxes && <Check size={11} />}
                    </button>
                    <span>
                      Apply these dimensions to upcoming boxes
                      <span style={{ display: "block", fontSize: 11.5, color: "#8b9296", marginTop: 3 }}>
                        Copies dimensions and weight only. You'll still choose items and quantities for each box.
                      </span>
                    </span>
                  </label>
                  {replicateBoxes && (
                    <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12.5, color: "#c3c8cc" }}>
                        Apply to next {numberOfBoxesToReplicate} box{numberOfBoxesToReplicate === 1 ? "" : "es"}
                      </span>
                      <QuantityStepper
                        value={numberOfBoxesToReplicate}
                        onChange={handleReplicateBoxCountChange}
                        min={1}
                        max={totalBox - activeStep}
                        size="sm"
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {isReview && (
            <>
              <ShipmentBoxesReview boxes={createFormData} isMobile={isMobile} />
              <ShipmentRulesReview
                shipmentValue={estimatedShipmentValue}
                acknowledged={rulesAcknowledged}
                onAcknowledgedChange={setRulesAcknowledged}
                isMobile={isMobile}
              />
            </>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 10, marginTop: 18 }}>
            {activeStep > 0 && (
              <Button variant="outline" onClick={handleWizardBack} disabled={createSubmitting}>
                Back
              </Button>
            )}
            {dialogMode && activeStep === 0 && (
              <Button variant="outline" onClick={onCancel} disabled={createSubmitting}>
                Cancel
              </Button>
            )}
            {isReview ? (
              <Button onClick={handleCreateSubmit} disabled={createSubmitting || !rulesAcknowledged}>
                {createSubmitting ? "Creating..." : "Create Shipment"}
              </Button>
            ) : activeStep === 0 ? (
              <Button onClick={handleWizardNext} disabled={!totalBox}>
                Next
              </Button>
            ) : (
              <Button
                onClick={handleWizardNext}
                disabled={
                  boxPhase === "items"
                    ? !box?.box_items?.length || box.box_items.some((i) => !(i.quantity > 0)) || hasCurrentBoxItemLimitErrors
                    : !isBoxComplete(boxIndex) || hasCurrentBoxRuleErrors
                }
              >
                {nextButtonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateShipmentPage;
